import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { GameProvider, useGame } from "./contexts/GameContext";
import { Toaster } from "./components/ui/sonner";

// Components
import AttractScreen from "./components/AttractScreen";
import RegistrationForm from "./components/RegistrationForm";
import GameView from "./components/GameView";
import EndScreen from "./components/EndScreen";
import AdminPanel from "./components/AdminPanel";
import TVLeaderboard from "./components/TVLeaderboard";

// Main Game App with view switching
const GameApp = () => {
  const { currentView, settings, resetToAttract } = useGame();

  // Inactivity timeout for attract screen reset
  useEffect(() => {
    if (currentView === 'admin') return; // Don't reset from admin

    let inactivityTimer;
    const timeout = (settings?.inactivity_timeout || 10) * 1000;

    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      if (currentView !== 'attract' && currentView !== 'game' && currentView !== 'admin') {
        inactivityTimer = setTimeout(() => {
          resetToAttract();
        }, timeout);
      }
    };

    // Events to track activity
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, resetTimer, { passive: true });
    });

    resetTimer();

    return () => {
      clearTimeout(inactivityTimer);
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [currentView, settings, resetToAttract]);

  // Apply badge styles from settings via direct DOM manipulation
  // CSS cannot override inline !important styles, so we modify the element directly
  useEffect(() => {
    if (!settings) return;
    const textColor = settings.badge_text_color || '#FFFFFF';
    const bgColor = settings.badge_bg_color || '#000000';
    const width = parseInt(settings.badge_width) || 200;
    const fontSize = parseInt(settings.badge_font_size) || 12;
    const opacity = settings.badge_opacity ?? 1;
    const height = settings.badge_height && settings.badge_height !== 'auto' ? parseInt(settings.badge_height) : null;

    function styleBadge() {
      const badge = document.getElementById('emergent-badge');
      if (!badge) return;
      badge.style.setProperty('background', bgColor, 'important');
      badge.style.setProperty('width', width + 'px', 'important');
      badge.style.setProperty('font-size', fontSize + 'px', 'important');
      badge.style.setProperty('pointer-events', 'none', 'important');
      badge.style.setProperty('opacity', String(opacity), 'important');
      if (height) {
        badge.style.setProperty('height', height + 'px', 'important');
        badge.style.setProperty('line-height', height + 'px', 'important');
      }
      // Style children (icon + text)
      badge.querySelectorAll('*').forEach(child => {
        child.style.setProperty('color', textColor, 'important');
        child.style.setProperty('fill', textColor, 'important');
      });
    }

    // Apply immediately
    styleBadge();
    // Re-apply periodically (badge might be re-injected by platform)
    const interval = setInterval(styleBadge, 2000);
    // Also watch for DOM changes
    const observer = new MutationObserver(styleBadge);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [settings]);

  // Render current view
  const renderView = () => {
    // Admin panel ha il suo container separato
    if (currentView === 'admin') {
      return <AdminPanel />;
    }
    
    switch (currentView) {
      case 'attract':
        return <AttractScreen />;
      case 'form':
        return <RegistrationForm />;
      case 'game':
        return <GameView />;
      case 'endscreen':
        return <EndScreen />;
      default:
        return <AttractScreen />;
    }
  };

  // Admin ha layout completamente diverso
  if (currentView === 'admin') {
    return <AdminPanel />;
  }

  return (
    <div className="App min-h-screen bg-[#0F172A]">
      {renderView()}
    </div>
  );
};

function App() {
  return (
    <GameProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<GameApp />} />
          <Route path="/leaderboard" element={<TVLeaderboard />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" />
    </GameProvider>
  );
}

export default App;
